import { useContext, useEffect, useRef } from "react";
import {
  AudioPlayerContext,
  type AudioPlayerSource,
  type AudioPlayerSourcesProviderRegistration,
} from "./context.js";

interface UseAudioPlayerProps {
  sources?: readonly AudioPlayerSource[];
}

const reactiveSourceProps: readonly (keyof AudioPlayerSource)[] = [
  "key",
  "title",
  "subtitle",
  "sortTimestamp",
  // A refreshed URL (such as a re-signed presigned URL) is a real change, not a render loop
  "src",
];

export function useAudioPlayer(props: UseAudioPlayerProps = {}) {
  const context = useContext(AudioPlayerContext);
  const { registerSourcesProvider } = context;

  // Register with the audio player context
  const registrationRef = useRef<
    AudioPlayerSourcesProviderRegistration | undefined
  >(undefined);
  useEffect(() => {
    const registration = registerSourcesProvider();
    registrationRef.current = registration;

    return () => {
      if (registrationRef.current === registration) {
        registrationRef.current = undefined;
      }
      registration.unregister();
    };
  }, [registerSourcesProvider]);

  // Update our sources on the audio player context
  const prevSourcesRef = useRef<readonly AudioPlayerSource[]>([]);
  const unstableRenderCountRef = useRef(0);
  useEffect(() => {
    const registration = registrationRef.current;
    if (!registration) {
      return;
    }

    const prevSources = prevSourcesRef.current;
    const nextSources = props.sources ?? [];
    prevSourcesRef.current = nextSources;

    // We currently require the sources to be stable/memoized to avoid a render loop
    // TODO: Memoize the sources automatically so we don't need to check for this
    if (
      // Identity is the not same
      nextSources !== prevSources &&
      // But everything else is the same
      nextSources.length > 0 &&
      nextSources.length === prevSources.length &&
      nextSources.every((source, index) => {
        const prevSource = prevSources[index];
        return (
          prevSource !== undefined &&
          reactiveSourceProps.every((prop) => source[prop] === prevSource[prop])
        );
      }) &&
      // HMR may cause false flags, so require a few iterations before throwing
      ++unstableRenderCountRef.current >= 10
    ) {
      if (import.meta.env.PROD) {
        // Break the loop
        return;
      } else {
        // Throw in development
        throw new Error(
          "Detected render loop in audio player, useAudioPlayer sources not properly memoized",
        );
      }
    }

    registration.onSourcesChange(props.sources ?? []);
  }, [props.sources]);

  return context;
}
